import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, hasPermission } from "@/lib/auth";
import { cashScopeKey } from "./use-branch-cash";
import { workbenchRequest } from "@/components/branch-cash/cashWorkbench";
import type { CountDenominations, DenominationValue } from "@/lib/cash-denominations";

export type CountType = "shift_handover" | "shift_receiving" | "spot_check" | "end_of_day" | "legacy";
export type CountReason = "missing_cash" | "mix_up" | "suspected_theft" | "other";
export type CountSession = { id: string; startedAt: string; cashBoxId: string; snapshotToken: string; receivers: { id: string; name: string; kind?: "user" | "receiver"; hasPin?: boolean; usual?: boolean }[]; denominationValues: DenominationValue[] };
export type CashCount = {
  id: string; cashBoxId: string; branchId?: string; branchName?: string; businessDate: string;
  countedAt: string; startedAt?: string; expectedAmount: string; countedAmount: string; differenceAmount: string;
  countType: CountType; countReason?: CountReason | null; reason?: string | null; note?: string | null;
  giverName?: string | null; countedBy?: string | null; createdByUserId?: string;
  countedWith?: string | null; ledgerSequenceAtCount?: number;
  receiverUserId?: string | null; receiverName?: string | null;
  receiverCashReceiverId?: string | null; receiverConfirmationMethod?: string | null;
  receiverPinConfirmedAt?: string | null; receiverConfirmationActorUserId?: string | null;
  receiverResponse?: "pending" | "confirmed" | "rejected" | null; receiverResponseNote?: string | null;
  adjustmentMovementId?: string | null; settledAt?: string | null; settlementMode?: string | null; settlementNote?: string | null;
  resolution?: { resolvedAt: string; resolvedByUserId: string; details: { reason: string; mode: string; movementId?: string } } | null;
  resolved?: boolean; canSettle?: boolean; photos?: { id: string }[];
  denominations?: CountDenominations | null;
};
export type CountPayload = {
  sessionId: string; snapshotToken: string; countedAmount: string; countType: Exclude<CountType, "legacy">;
  receiverUserId?: string; receiverCashReceiverId?: string; countReason?: CountReason; note?: string; idempotencyKey: string;
  denominations: CountDenominations;
};
export type CountPolicy = { policy: "finance_settles" | "post_automatically"; canManage: boolean };
export const validCountAmount = (value: string) => /^\d{1,12}(?:\.\d{1,2})?$/.test(value);
export function canRespondToCount(count: CashCount, userId?: string) {
  return !!userId && count.countType === "shift_handover" && count.receiverUserId === userId &&
    count.createdByUserId !== userId && (!count.receiverResponse || count.receiverResponse === "pending");
}
export function useCashCountAccess() {
  const { currentWorkspace, user, roles } = useAuth();
  return {
    workspaceId: currentWorkspace?.id, userId: user?.id,
    canFinance: hasPermission(user, "branch_cash.manage", roles.map(r => ({ ...r, isSystem: false }))),
    scope: cashScopeKey(currentWorkspace?.id, user, roles.find(r => r.id === user?.roleId)?.permissions.map(p => p.id)),
  };
}
export function useCashCount(id?: string) {
  const { workspaceId, userId, scope } = useCashCountAccess();
  return useQuery<CashCount>({
    queryKey: ["branch-cash", "count-detail", ...scope, id],
    enabled: !!workspaceId && !!userId && !!id,
    queryFn: () => workbenchRequest("GET", `/api/cash/counts/${encodeURIComponent(id!)}?workspaceId=${encodeURIComponent(workspaceId!)}`),
  });
}
export type CountMissingEntry = { id: string; reference: string; amount: string; direction: "in" | "out"; occurredAt: string; notes: string | null };
export function useCashCountMissingEntries(id: string) {
  const { workspaceId, userId, canFinance, scope } = useCashCountAccess();
  return useQuery<{ entries: CountMissingEntry[] }>({
    queryKey: ["branch-cash", "count-missing-entries", ...scope, id],
    enabled: !!workspaceId && !!userId && canFinance,
    queryFn: () => workbenchRequest("GET", `/api/cash/counts/${encodeURIComponent(id)}/missing-entries?workspaceId=${encodeURIComponent(workspaceId!)}`),
  });
}
export function useCashCountActions() {
  const { workspaceId } = useCashCountAccess();
  const cache = useQueryClient();
  const refresh = () => cache.invalidateQueries({ queryKey: ["branch-cash"] });
  const start = useMutation<CountSession, Error, { cashBoxId: string; idempotencyKey: string }>({
    // Preparation only: server returns an encrypted snapshot, never persists a session.
    // The first session/financial write is save, using this same snapshotToken.
    mutationFn: payload => workbenchRequest("POST", "/api/cash/count-sessions", { workspaceId, ...payload }),
  });
  const save = useMutation<CashCount, Error, CountPayload>({
    mutationFn: payload => workbenchRequest("POST", "/api/cash/counts", { workspaceId, ...payload }),
    onSuccess: refresh,
  });
  const respond = useMutation<CashCount, Error, { id: string; response: "confirmed" | "rejected"; note?: string }>({
    mutationFn: ({ id, ...payload }) => workbenchRequest("POST", `/api/cash/counts/${encodeURIComponent(id)}/respond`, { workspaceId, ...payload }),
    onSuccess: refresh,
  });
  const settle = useMutation<CashCount, Error, { id: string; mode: "post" | "link" | "review"; note: string; movementId?: string }>({
    mutationFn: ({ id, ...payload }) => workbenchRequest("POST", `/api/cash/counts/${encodeURIComponent(id)}/settle`, { workspaceId, ...payload }),
    onSuccess: refresh,
  });
  return { start, save, respond, settle };
}
export function useCashCountPolicy() {
  const { workspaceId, userId, canFinance, scope } = useCashCountAccess();
  const cache = useQueryClient();
  const key = ["branch-cash", "count-policy", ...scope];
  const query = useQuery<CountPolicy>({
    queryKey: key, enabled: !!workspaceId && !!userId && canFinance,
    queryFn: () => workbenchRequest("GET", `/api/cash/count-policy?workspaceId=${encodeURIComponent(workspaceId!)}`),
  });
  const save = useMutation<CountPolicy, Error, CountPolicy["policy"]>({
    mutationFn: policy => workbenchRequest("PUT", "/api/cash/count-policy", { workspaceId, policy }),
    onSuccess: data => { cache.setQueryData(key, data); },
  });
  return { query, save, canFinance };
}
export type UnfinishedCountSession = { id: string; startedAt: string; closedAt: string | null; closeReason: string | null; completedCountId: string | null };
export function useUnfinishedCountSessions(cashBoxId: string) {
  const { workspaceId, userId, scope } = useCashCountAccess();
  const client = useQueryClient();
  const query = useQuery<{ sessions: UnfinishedCountSession[]; canClose: boolean }>({
    queryKey: ["branch-cash", "count-sessions", ...scope, cashBoxId],
    enabled: !!workspaceId && !!userId && !!cashBoxId,
    queryFn: () => workbenchRequest("GET", `/api/cash/count-sessions?cashBoxId=${encodeURIComponent(cashBoxId)}&workspaceId=${encodeURIComponent(workspaceId!)}`),
  });
  const close = useMutation<unknown, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => workbenchRequest("POST", `/api/cash/count-sessions/${encodeURIComponent(id)}/close`, { workspaceId, reason }),
    onSuccess: () => client.invalidateQueries({ queryKey: ["branch-cash"] }),
  });
  return { query, close };
}
export function useCashDenominations() {
  const { workspaceId, userId, canFinance, scope } = useCashCountAccess();
  const cache = useQueryClient();
  const key = ["branch-cash", "denominations", ...scope];
  const query = useQuery<{ values: DenominationValue[]; canManage: boolean }>({
    queryKey: key, enabled: !!workspaceId && !!userId,
    queryFn: () => workbenchRequest("GET", `/api/cash/denominations?workspaceId=${encodeURIComponent(workspaceId!)}`),
  });
  const save = useMutation<{ values: DenominationValue[]; canManage: boolean }, Error, DenominationValue[]>({
    mutationFn: values => workbenchRequest("PUT", `/api/cash/denominations?workspaceId=${encodeURIComponent(workspaceId!)}`, { values }),
    onSuccess: async data => {
      cache.setQueryData(key, data);
      await cache.invalidateQueries({ queryKey: ["branch-cash", "denominations"] });
    },
  });
  return { query, save, canFinance };
}